/**
 * DemoModeContext
 * Global context for demo mode state (like Stripe's test mode)
 * Provides demo status to all screens and handles demo actions.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { demoApi } from '../api/endpoints/demo';
import { useAuthStore } from '../store/authStore';
import logger from '../utils/logger';

interface DemoModeState {
  isLoading: boolean;
  demoMode: boolean;
  profileName: string | null;
}

interface DemoModeContextValue extends DemoModeState {
  refreshDemoStatus: () => Promise<void>;
  exitDemoMode: () => Promise<void>;
}

const DemoModeContext = createContext<DemoModeContextValue | undefined>(undefined);

interface DemoModeProviderProps {
  children: ReactNode;
}

export function DemoModeProvider({ children }: DemoModeProviderProps) {
  const { isAuthenticated } = useAuthStore();
  const [state, setState] = useState<DemoModeState>({
    isLoading: false,
    demoMode: false,
    profileName: null,
  });

  const refreshDemoStatus = useCallback(async () => {
    if (!isAuthenticated) {
      logger.info('[DemoModeContext] Not authenticated, skipping demo status fetch');
      setState({ isLoading: false, demoMode: false, profileName: null });
      return;
    }
    try {
      logger.info('[DemoModeContext] Fetching demo status...');
      setState(prev => ({ ...prev, isLoading: true }));
      const status = await demoApi.getStatus();
      logger.info('[DemoModeContext] Demo status received:', status);
      setState({
        isLoading: false,
        demoMode: status.demo_mode,
        profileName: status.profile_name,
      });
    } catch (err) {
      logger.error('[DemoModeContext] Failed to fetch demo status:', err);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [isAuthenticated]);

  const exitDemoMode = useCallback(async () => {
    try {
      await demoApi.purge();
      setState({
        isLoading: false,
        demoMode: false,
        profileName: null,
      });
    } catch (err) {
      logger.error('[DemoModeContext] Failed to exit demo mode:', err);
      throw err;
    }
  }, []);

  // Fetch demo status when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshDemoStatus();
    } else {
      setState({ isLoading: false, demoMode: false, profileName: null });
    }
  }, [isAuthenticated, refreshDemoStatus]);

  return (
    <DemoModeContext.Provider
      value={{
        ...state,
        refreshDemoStatus,
        exitDemoMode,
      }}
    >
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode(): DemoModeContextValue {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}

// Helper hook for conditional rendering
export function useIsDemoMode(): boolean {
  const { demoMode } = useDemoMode();
  return demoMode;
}
